import { useState, useEffect } from "react";
import "../housestyle.css";
import "./Ranks.css";
import * as Icons from "../Icons/Icons";
import api from "../API/Api";
import PageHero from "../components/PageHero";

const HERO_VIDEO = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_3.mp4';

// Maps rank name (lowercase) from DB → local icon import
const RANK_ICONS = {
  zombie: Icons.zombie,
  skeleton: Icons.skeleton,
  creeper: Icons.creeper,
  blaze: Icons.blaze,
  breeze: Icons.breeze,
  phantom: Icons.phantom,
  warden: Icons.warden,
};

function calcDiscounted(price, percent) {
  return (price * (1 - percent / 100)).toFixed(2);
}

function BuyModal({ rank, saleCode, onClose }) {
  const [copied, setCopied] = useState(false);
  const [username, setUsername] = useState("");
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const pct = rank.discount_percent;
  const discountedPrice = pct ? calcDiscounted(rank.price, pct) : null;
  const showCode = saleCode && pct;

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function copy() {
    navigator.clipboard.writeText(saleCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleCheckout() {
    if (!username || username.trim().length < 3) {
      setCheckoutError("Please enter a valid Minecraft username.");
      return;
    }
    
    setIsCheckingOut(true);
    setCheckoutError("");

    try {
      // Create basket
      const basketRes = await fetch(`https://headless.tebex.io/api/accounts/${window.TEBEX_PUBLIC_KEY}/baskets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          complete_url: window.location.origin + "/store/success",
          cancel_url: window.location.origin + "/store/cancel",
          username: username.trim()
        })
      });

      if (!basketRes.ok) throw new Error("Failed to create checkout basket");
      const basketData = await basketRes.json();
      const basketIdent = basketData.data.ident;

      // Ensure we have a tebex package ID mapped
      // For now, if the DB doesn't have it, we use a placeholder or fallback. 
      // Replace these with your actual Tebex package IDs!
      const packageMap = {
        zombie: 7352841,
        skeleton: 7387897,
        creeper: 7387900,
        blaze: 7387902,
        breeze: 7387905,
        phantom: 7387910,
        warden: 7387912,
      };
      
      const packageId = rank.tebex_id || packageMap[rank.name.toLowerCase()];
      
      if (!packageId) {
         throw new Error("Tebex Package ID not found for this rank.");
      }

      // Add package to basket
      const packageRes = await fetch(`https://headless.tebex.io/api/baskets/${basketIdent}/packages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: packageId,
          quantity: 1
        })
      });

      if (!packageRes.ok) throw new Error("Failed to add rank to basket");
      
      const packageData = await packageRes.json();
      const checkoutUrl = packageData.data.links.checkout;

      // Redirect to Tebex checkout page
      window.location.href = checkoutUrl;

    } catch (err) {
      console.error(err);
      setCheckoutError(err.message || "An error occurred during checkout.");
      setIsCheckingOut(false);
    }
  }

  const icon = RANK_ICONS[rank.name.toLowerCase()];

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div
        className="modal-box"
        style={{ borderTop: `3px solid ${rank.color}` }}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="modal-header">
          <div
            className="modal-icon-wrap"
            style={{ filter: `drop-shadow(0 0 14px ${rank.color})` }}
          >
            {icon && <img src={icon} alt={rank.name} className="modal-icon" />}
          </div>
          <div>
            <div className="modal-rank-name" style={{ color: rank.color }}>
              {rank.name.charAt(0).toUpperCase() + rank.name.slice(1)}
            </div>
            <div className="modal-price-row">
              {discountedPrice ? (
                <>
                  <span className="modal-price-old">
                    SOON!
                  </span>
                  {/* <span className="modal-price-old">
                    €{rank.price.toFixed(2)}
                  </span>
                  <span className="modal-price-new">€{discountedPrice}</span>
                  <span
                    className="modal-badge"
                    style={{ background: rank.color }}
                  >
                    -{pct}%
                  </span> */}
                </>
              ) : (
                // <span className="modal-price-new">
                //   €{rank.price.toFixed(2)}
                // </span>
                <span className="modal-price-new">SOON!</span>
              )}
            </div>
          </div>
        </div>

        <div className="modal-perks-label">Included perks</div>
        <div className="modal-perks">
          {rank.perks.map((p) => (
            <div key={p} className="modal-perk">
              <span className="modal-perk-check" style={{ color: rank.color }}>
                ✓
              </span>
              {p}
            </div>
          ))}
        </div>

        {showCode && (
          <div className="modal-code-row">
            <span className="modal-code-label">Use code at checkout:</span>
            <button className="modal-code-btn" onClick={copy}>
              <span className="modal-code-text">{saleCode}</span>
              <span className="modal-code-copy">
                {copied ? "Copied!" : "Copy"}
              </span>
            </button>
          </div>
        )}

        <div className="modal-checkout-section" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label className="modal-ign-label">Minecraft Username</label>
          <input
            type="text"
            className="modal-ign-input"
            placeholder="Notch"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          {checkoutError && <div style={{ color: 'var(--c-danger)', fontSize: '14px' }}>{checkoutError}</div>}
        </div>

        <div className="modal-actions" style={{ marginTop: '20px' }}>
          <button className="btn modal-cancel-btn" onClick={onClose} disabled={isCheckingOut}>
            Cancel
          </button>
          <button
            onClick={handleCheckout}
            // disabled={isCheckingOut}
            disabled={true} // Temporarily disable
            className="btn btn-primary modal-buy-btn"
            style={{ background: rank.color, opacity: isCheckingOut ? 0.7 : 1, cursor: isCheckingOut ? 'not-allowed' : 'pointer' }}
          >
            {isCheckingOut ? "Processing..." : "Checkout"}
          </button>
        </div>

        <p className="modal-disclaimer">
          Ranks are permanent and carry over between seasons. No refunds after
          rank has been applied.
        </p>
      </div>
    </div>
  );
}

// Put your Tebex Public API Key here (found in Creator Panel -> Integrations -> API -> Headless API)
window.TEBEX_PUBLIC_KEY = "12b7l-0438dcd7123eb1aa7d095ca0182f285b8f78e706";

export default function Ranks() {
  const [ranks, setRanks] = useState([]);
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRank, setSelectedRank] = useState(null);

  useEffect(() => {
    api("getranks").then((res) => {
      if (res.isSuccess && res.data) {
        setRanks(res.data.ranks ?? []);
        setSale(res.data.sale ?? null);
      }
      setLoading(false);
    });
  }, []);

  return (
    <>
      <PageHero
        video={HERO_VIDEO}
        eyebrow="Support the Server"
        title="Ranks & "
        em="Store"
        subtitle="Purchase a rank to support SimpleMC and unlock exclusive perks, cosmetics and bonuses in-game."
        height={"300px"}
      >
      </PageHero>

      <div className="container section">
        <div className="section-label">
          <span className="sl-text">All Ranks</span>
        </div>

        {loading ? (
          <div className="ranks-loading">Loading ranks…</div>
        ) : (
          <div className="ranks-grid">
            {ranks.map((rank, i) => {
              const pct = rank.discount_percent;
              const discountedPrice = pct
                ? calcDiscounted(rank.price, pct)
                : null;
              const icon = RANK_ICONS[rank.name.toLowerCase()];
              const displayName =
                rank.name.charAt(0).toUpperCase() + rank.name.slice(1);

              return (
                <div
                  key={rank.id}
                  className="rank-card card fade-up"
                  style={{ animationDelay: `${i * 0.06}s` }}
                >
                  <div
                    className="rank-card-top"
                    style={{ borderColor: rank.color + "55" }}
                  >
                    <div
                      className="rank-card-top-glow"
                      style={{ background: rank.color }}
                    />
                    <div
                      className="rank-icon-wrap"
                      style={{ filter: `drop-shadow(0 0 15px ${rank.color})` }}
                    >
                      {icon && (
                        <img
                          src={icon}
                          alt={displayName}
                          className="rank-icon"
                        />
                      )}
                    </div>
                    <div>
                      <div className="rank-name" style={{ color: rank.color }}>
                        {displayName}
                      </div>
                      {discountedPrice ? (
                        // <div className="rank-price-group">
                        //   <span className="rank-price-old">
                        //     €{rank.price.toFixed(2)}
                        //   </span>
                        //   <span className="rank-price">€{discountedPrice}</span>
                        //   <span
                        //     className="rank-discount-badge"
                        //     style={{ background: rank.color }}
                        //   >
                        //     -{pct}%
                        //   </span>
                        // </div>
                          <div className="rank-price-group">
                            <span className="rank-price">
                              SOON!
                            </span>
                          </div>
                      ) : (
                        // <div className="rank-price">
                        //   €{rank.price.toFixed(2)}
                        // </div>
                        <div className="rank-price">
                          SOON!
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rank-perks">
                    {rank.perks.map((p) => (
                      <div key={p} className="rank-perk">
                        <span
                          className="rank-perk-check"
                          style={{ color: rank.color }}
                        >
                          ✓
                        </span>
                        {p}
                      </div>
                    ))}
                  </div>

                  <button
                    className="btn btn-primary rank-buy-btn"
                    style={{ background: rank.color }}
                    onClick={() => setSelectedRank(rank)}
                  >
                    Buy {displayName}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="section-label" style={{ marginTop: '5rem' }}>
          <span className="sl-text">Compare Features</span>
        </div>

        <div className="rank-compare-wrap fade-up">
          <div className="rank-compare-table-scroll">
            <table className="rank-compare-table">
              <thead>
                <tr>
                  <th>Features</th>
                  {ranks.map(r => (
                    <th key={r.id} style={{ color: r.color }}>
                      <div className="rank-compare-header">
                        {(r.name.toLowerCase() === 'creeper' || r.name.toLowerCase() === 'blaze') && (
                          <span className="rank-badge popular">Most Popular</span>
                        )}
                        {(r.name.toLowerCase() === 'phantom' || r.name.toLowerCase() === 'warden') && (
                          <span className="rank-badge value">Best Value</span>
                        )}
                        {r.name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Homes</td>
                  {ranks.map(r => <td key={r.id}>{r.name === 'Zombie' ? '2' : r.name === 'Skeleton' ? '3' : r.name === 'Creeper' ? '5' : r.name === 'Blaze' ? '8' : r.name === 'Breeze' ? '12' : 'Unlimited'}</td>)}
                </tr>
                <tr>
                  <td>Vault Slots</td>
                  {ranks.map(r => <td key={r.id}>{r.name === 'Zombie' ? '1' : r.name === 'Skeleton' ? '2' : r.name === 'Creeper' ? '3' : r.name === 'Blaze' ? '5' : '9'}</td>)}
                </tr>
                <tr>
                  <td>Custom Suffix</td>
                  {ranks.map(r => <td key={r.id}>{['Zombie', 'Skeleton'].includes(r.name) ? '✕' : '✓'}</td>)}
                </tr>
                <tr>
                  <td>Chat Colors</td>
                  {ranks.map(r => <td key={r.id}>✓</td>)}
                </tr>
                <tr>
                  <td>Priority Queue</td>
                  {ranks.map(r => <td key={r.id}>{['Phantom', 'Warden', 'Breeze'].includes(r.name) ? '✓' : '✕'}</td>)}
                </tr>
                <tr>
                  <td>Daily Rewards</td>
                  {ranks.map(r => <td key={r.id}>✓</td>)}
                </tr>
                <tr>
                  <td>Exclusive Emotes</td>
                  {ranks.map(r => <td key={r.id}>{['Zombie', 'Skeleton', 'Creeper'].includes(r.name) ? '✕' : '✓'}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="ranks-disclaimer card-raised">
          <img
            src={Icons.info}
            alt="Disclaimer"
            style={{ width: "30px" }}
            className="ranks-disclaimer-icon"
          />
          <p>
            Ranks are permanent and carry over between seasons unless stated
            otherwise. All purchases support server costs. By purchasing you
            agree to our terms — no refunds after the rank has been applied.
          </p>
        </div>
      </div>

      {selectedRank && (
        <BuyModal
          rank={selectedRank}
          saleCode={sale?.active ? sale.code : null}
          onClose={() => setSelectedRank(null)}
        />
      )}
    </>
  );
}
